import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../loader/Loader';
import { Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import "./DayEndPopup.css"
import { completeDayEnd, getDayEndStatusFromSession, updateDayEndStatusInSession, updateLasteEndDate } from './DayEndUtils';
import dayjs from 'dayjs';
import CustomGenericModal from './CustomGenericModal';
import { GenericModal, getGenericModalProps, initialGenericModalProps } from '../../features/viewOrder/ViewOrderUtils';
import { sendSlackAlert, SlackChannel } from '../../services/slackService';
import { safeStringify } from './DmsUtils';

export enum StatusType {
  NotStarted = "notStarted",
  InProgress = "inProgress",
  Completed = "completed",
}

const DayEndBanner: React.FC = () => {
  const navigate = useNavigate();
  const initialOrderCount = {
    pending: 0,
    invoiced: 0,
    outForDelivery: 0,
    startDate: "",
    pendingDays: 0,
    status: ["PENDING, INVOICES, OUT FOR DELIVERY"],
  };

  const [orderCount, setOrderCount] = useState(initialOrderCount);
  const [loader, setLoader] = useState(true);
  const [status, setStatus] = useState<any>({});
  const [genericModalProps, setGenericModalProps] =
    useState<GenericModal>(initialGenericModalProps);
  const [message, setMessage] = useState("");

  const getData = async () => {
    try {
      setLoader(true);

      await updateDayEndStatusInSession();
      const dayEndObj = getDayEndStatusFromSession();

      if (dayEndObj) {

        setOrderCount({
          pending: dayEndObj.pendingCount,
          invoiced: dayEndObj.invoicedCount,
          outForDelivery: dayEndObj.outForDeliveryCount,
          startDate: dayEndObj.startDate.split(" ")[0],
          pendingDays: dayjs(dayEndObj.endDate).diff(dayjs(dayEndObj.startDate), "day") + 1,
          status: dayEndObj.mandatoryStatus
        });

        const m: string[] = []

        dayEndObj.mandatoryStatus.forEach((status: string) => {

          if (status === "PENDING") {
            m.push("Invoice");
          }
          if (status === "INVOICED") {
            m.push("Loadout");
          }
          if (status === "OUT FOR DELIVERY") {
            m.push("Settlement");
          }
        });

        setMessage(m.join(" -> "));

        const task1Status = getProgressStatus(dayEndObj.status.invoice)
        const task2Status = getProgressStatus(dayEndObj.status.loadout)
        const task3Status = getProgressStatus(dayEndObj.status.delivery)
        setStatus({
          task1Status,
          task2Status,
          task3Status,
        });
      }
    } catch (error) {
      console.error("Error fetching order data for day end:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);
  const getProgressStatus = (statusType: string) => {
    if (statusType === StatusType.NotStarted) {
      return {
        label: "Start Task",
        className: "start-button",
      };
    } else if (statusType === StatusType.InProgress) {
      return {
        label: "In Progress",
        className: "in-progress-button",
      };
    }
    return {
      label: "Completed",
      className: "completed-button",
    };
  };

  const handleClickTask = (taskNumber: number) => {
    // Logic to navigate to respective module based on task number
    if (taskNumber === 1) {
      if (status.task1Status.label === "Completed") {
        return;
      }
      navigate("/dashboard/sale/manageOrders");
    } else if (taskNumber === 2) {
      if (status.task2Status.label === "Completed") {
        return;
      }
      navigate("/dashboard/delivery/loadouts/preseller");
    } else if (taskNumber === 3) {
      if (status.task3Status.label === "Completed") {
        return;
      }
      navigate("/dashboard/manageLoadout");
    }
  };

  const resetGenericModalProps = () => setGenericModalProps(initialGenericModalProps);

  const markDayEnd = async () => {
    await updateDayEndStatusInSession()
    const newDayEndObj = getDayEndStatusFromSession();
    if (newDayEndObj && !newDayEndObj.canMarkDayEnd) {
      setGenericModalProps(getGenericModalProps(
        "positive",
        "Please complete all the activites"
      ))
      return;
    }

    //update last day end in table
    try{

      await updateLasteEndDate()
      setGenericModalProps(getGenericModalProps(
        "success",
        "Day End marked successfully",
        "Continue",
        "",
        () => {
          resetGenericModalProps();
          navigate("/dashboard/sale/manageOrders");
        },
      ))
      await completeDayEnd();
    } catch (error: any) {
      if (error?.message === "alreadyMarked"){
        setGenericModalProps(getGenericModalProps(
          "negative",
          "Day end already marked"
        ))
        await completeDayEnd();
      } else {
        setGenericModalProps(getGenericModalProps(
          "negative",
          "Failed to mark Day End. Please try again."
        ))
      }
      await sendSlackAlert(
        SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
        "Errow while marking dayend",
        `Error: ${(error as Error).message}`,
        safeStringify({ error })
      );
    }
  }
  if (loader) return <Loader />;

  return (
    <div style={styles.container}>
      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        rightButtonText={genericModalProps?.rightButtonText}
        modalOpen={genericModalProps?.open}
        onLeftButtonClick={genericModalProps?.onLeftButtonClick}
        onRightButtonClick={genericModalProps?.onRightButtonClick}
        leftButtonPadding={genericModalProps?.leftButtonPadding}
        onClose={resetGenericModalProps}
        width={genericModalProps?.width}
      />
      {/* Header Section */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <svg
            style={styles.headerIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="12" cy="12" r="10" strokeWidth="2" />
            <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
            <circle
              cx="12"
              cy="16"
              r="0.5"
              fill="currentColor"
              strokeWidth="1"
            />
          </svg>
          <span style={styles.headerTitle}>Action required</span>
          <span style={styles.headerSubtitle}>
            (Complete these task to open all modules)
          </span>
        </div>
      </div>

      {/* Main Content Card */}
      <div style={styles.card}>
        {/* Card Header */}
        <div style={styles.cardHeader}>
          <div>
            <h3 style={styles.cardTitle}>Day end settlement required</h3>
            <p style={styles.cardDescription}>
              You have {orderCount.pendingDays} days of pending settlements. Please complete all
              activities to proceed.
            </p>
          </div>
          <div style={styles.badges}>
          
            <Button
              onClick={markDayEnd}
              variant="contained"
              startIcon={<CheckIcon sx={{ 
                ".& MuiSvgIcon-root ": {
                  fontSize: '12px' 
                }
              }} />}
              sx={{
                background: 'linear-gradient(90deg, #14b8a6 0%, #10b981 100%)',
                color: 'white',
                fontSize: '12px',
                fontWeight: 600,
                padding: '4px 8px',
                borderRadius: '50px',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #0d9488 0%, #059669 100%)',
                  boxShadow: '0 6px 16px rgba(20, 184, 166, 0.4)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Mark Day End
            </Button>
            <span style={styles.badge}>
              Last settlement date : <strong>{orderCount.startDate}</strong>
            </span>
            <span style={styles.badgePending}>
              Pending days : <strong>{orderCount.pendingDays} days</strong>
            </span>
          </div>
        </div>

        {/* Tasks */}
        
        <div style={styles.tasksContainer}>
          {/* Task 1 */}
          {orderCount.status.includes("PENDING") &&
            <div style={styles.taskItem}>
              <div style={styles.taskLeft}>
                <span style={styles.taskNumber}>
                  {[ orderCount.status.includes("PENDING") ? 1 : null,
                     orderCount.status.includes("INVOICED") || orderCount.status.includes("PARTIALLY INVOICED") ? 2 : null,
                     orderCount.status.includes("OUT FOR DELIVERY") ? 3 : null
                  ].filter(Boolean).indexOf(1) + 1}
                </span>
                <div style={styles.taskInfo}>
                  <div style={styles.taskTitleRow}>
                    <span style={styles.taskTitle}>
                      {`Pending Orders (${orderCount.pending})`}
                    </span>
                  </div>
                  <p style={styles.taskDescription}>
                    Review and complete pending invoices
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleClickTask(1)}
                className={status.task1Status.className}
                style={{ ...styles.taskButton }}
              >
                {status.task1Status.label}
              </button>
            </div>
          }
          {/* Task 2 */}
          {(orderCount.status.includes("INVOICED") || orderCount.status.includes("PARTIALLY INVOICED")) &&

            <div style={styles.taskItem}>
              <div style={styles.taskLeft}>
                <span style={styles.taskNumber}>
                  {[ orderCount.status.includes("PENDING") ? 1 : null,
                     orderCount.status.includes("INVOICED") || orderCount.status.includes("PARTIALLY INVOICED") ? 2 : null,
                     orderCount.status.includes("OUT FOR DELIVERY") ? 3 : null
                  ].filter(Boolean).indexOf(2) + 1}
                </span>
                <div style={styles.taskInfo}>
                  <div style={styles.taskTitleRow}>
                    <span style={styles.taskTitle}>
                      {`Loadout Creation (${orderCount.invoiced})`}
                    </span>
                  </div>
                  <p style={styles.taskDescription}>
                    Create and verify loadout records
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleClickTask(2)}
                className={status.task2Status.className}
                style={{ ...styles.taskButton }}
              >
                {status.task2Status.label}
              </button>
            </div>

          }
          {/* Task 3 */}
          {(orderCount.status.includes("OUT FOR DELIVERY")) &&
            <div style={styles.taskItem}>
              <div style={styles.taskLeft}>
                <span style={styles.taskNumber}>
                  {[ orderCount.status.includes("PENDING") ? 1 : null,
                     orderCount.status.includes("INVOICED") || orderCount.status.includes("PARTIALLY INVOICED") ? 2 : null,
                     orderCount.status.includes("OUT FOR DELIVERY") ? 3 : null
                  ].filter(Boolean).indexOf(3) + 1}
                </span>
                <div style={styles.taskInfo}>
                  <div style={styles.taskTitleRow}>
                    <span style={styles.taskTitle}>
                      {`Delivery Confirmation (${orderCount.outForDelivery})`}
                    </span>
                  </div>
                  <p style={styles.taskDescription}>
                    Settle and reconcile all invoices
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleClickTask(3)}
                className={status.task3Status.className}
                style={{ ...styles.taskButton }}
              >
                {status.task3Status.label}
              </button>
            </div>
          }
        </div>

        {/* Recommendation */}
        <div style={styles.recommendation}>
          <svg
            style={styles.warningIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="12" cy="12" r="10" strokeWidth="2" />
            <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
            <circle
              cx="12"
              cy="16"
              r="0.5"
              fill="currentColor"
              strokeWidth="1"
            />
          </svg>
          <span style={styles.recommendationText}>
            <strong>Recommendation : </strong>
            You should proceed step wise as given ({message}) in the pop up for the easy process
          </span>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  headerIcon: {
    width: '20px',
    height: '20px',
    color: '#ef4444',
    marginRight: '8px',
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    marginRight: '8px',
  },
  headerSubtitle: {
    fontSize: '14px',
    color: '#ef4444',
    fontWeight: '500'
  },
  card: {
    border: '1px solid #fecaca',
    borderRadius: '8px',
    backgroundColor: '#fef2f2',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '12px',
    paddingBottom: '0px',
    flexWrap: 'wrap' as const,
    gap: '16px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 4px 0',
  },
  cardDescription: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0,
  },
  badges: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  badge: {
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '13px',
    backgroundColor: '#fff7ed',
    color: '#A45914',
    border: '1px solid #fed7aa',
  },
  badgePending: {
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '13px',
    backgroundColor: '#fff7ed',
    color: '#A45914',
    border: '1px solid #fed7aa',
  },
  tasksContainer: {
    padding: '12px',
    paddingBottom : '0px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap' as const,
  },
  taskItem: {
    flex: '1 1 300px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    gap: "12px",
    backgroundColor: '#fff',
  },
  taskLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flex: 1,
  },
  taskNumber: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#4b5563',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: "#F2F2F2",
    borderRadius: "50%"
  },
  taskInfo: {
    flex: 1,
  },
  taskTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px',
  },
  taskTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
  },
  checkIcon: {
    width: '18px',
    height: '18px',
    color: '#22c55e',
  },
  infoIcon: {
    width: '18px',
    height: '18px',
    color: '#9ca3af',
  },
  taskDescription: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0,
  },
  taskButton: {
    padding: '8px 20px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.2s',
  },
  completedButton: {
    backgroundColor: "#E7FFE7",
    color: "#339D3A",
    border: "1px solid #339D3A",
  },
  inProgressButton: {
    backgroundColor: "#F2AE5A",
    color: "white",
    border: "none",
  },
  startButton: {
    color: '#fff',
    backgroundColor: "#ED6F6F",
    border: "none",
  },
  recommendation: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px',
  },
  warningIcon: {
    width: '18px',
    height: '18px',
    color: '#ef4444',
    marginRight: '8px',
    flexShrink: 0,
  },
  recommendationText: {
    fontSize: '13px',
    color: '#9a3412',
    lineHeight: '1.5',
  },
};

export default DayEndBanner;
